import { errorMessage } from "@streamoid/errors";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { notify } from "../../utils/utils";
import { CENTRAL_DASHBOARD_BACKEND_API } from "../../utils/envConfig";
import {
  deleteCleanupCandidates,
  excludeCleanupCandidates,
  getCleanupCandidateHits,
  getCleanupCandidates,
  getCleanupEnvironment,
  getCleanupSummary,
  restoreCleanupCandidates,
  reviewCleanupCandidates,
  runCleanupScan,
} from "../../utils/dataCleanup";
import "./style.css";

/*
 * Data cleanup: unused workspaces and users.
 *
 * Statuses as the backend names them, and as this page shows them:
 *   watching -> "To review"  listed by a scan, still works, use is recorded
 *   excluded -> "Kept"       taken off the list for good
 *   deleted  -> "Deleted"    removed; every document is in the archive, and
 *                            anyone coming back for it is recorded
 *   restored -> "Restored"   put back from the archive, exactly as it was
 */

const PAGE_SIZE = 50;

// `help` is the tab's tooltip.
const TABS = [
  {
    id: "watching",
    label: "To review",
    help: "Found by a scan. Still working — any use shows in the Used column.",
  },
  {
    id: "excluded",
    label: "Kept",
    help: "Off the list for good; scans skip them.",
  },
  {
    id: "deleted",
    label: "Deleted",
    help: "Removed. A copy of everything is in the archive; Restore puts it back.",
  },
  {
    id: "restored",
    label: "Restored",
    help: "Put back from the archive, exactly as they were.",
  },
] as const;

type Status = (typeof TABS)[number]["id"];

interface Candidate {
  _id: string;
  type: "workspace" | "user";
  refId: string;
  reason: string;
  snapshot?: {
    name?: string;
    email?: string;
    ownerEmail?: string;
    ownerWorkspaces?: number;
    createdAt?: string;
    lastDeductAt?: string | null;
    creditsExpireAt?: string | null;
  };
  status?: string;
  createdAt?: string;
  excludedAt?: string;
  deletedAt?: string;
  deletedBy?: { email?: string };
  restoredAt?: string;
  restoredBy?: { email?: string };
  usedCount?: number;
  lastUsedAt?: string;
  returnedCount?: number;
  lastReturnedAt?: string;
}

interface Hit {
  _id: string;
  kind: "used" | "returned";
  email?: string;
  userId?: string;
  /** Apps the requests came from (Origin host, or "API token"). */
  sources?: string[];
  paths?: string[];
  /** Older rows: a single path. */
  path?: string;
  lastAt?: string;
}

interface ScanResult {
  workspacesScanned: number;
  usersScanned: number;
  workspacesListed: number;
  usersListed: number;
  alreadyListed: number;
  billingFailed: number;
  billingUnknown: number;
}

interface Environment {
  nodeEnv?: string | null;
  database?: { name?: string | null; host?: string | null };
  billing?: { url?: string | null; reachable?: boolean };
}

type Summary = Record<Status, { workspace: number; user: number }>;

interface Plan {
  candidateId: string;
  name: string;
  skip?: string | null;
  counts?: Record<string, number>;
}

interface DeleteResult {
  deleted: Plan[];
  skipped: Plan[];
  failed: { name: string; error: string; partlyDeleted?: boolean }[];
}

interface RestoreResult {
  restored: Plan[];
  skipped: Plan[];
}

// Button tooltips, and the text of their confirmations.
const DELETE_HELP =
  "Removes them. A copy of everything goes to the archive first; Restore puts it back. Billing and stores are not touched.";
const KEEP_HELP =
  "Still needed: take them off the list. Scans won't add them again.";
const REVIEW_HELP =
  "Puts them back under review, so they can be deleted later.";
const RESTORE_HELP =
  "Puts everything back from the archive, exactly as it was.";

// What the delete preview counts, in plain words.
const PARTS: Record<string, string> = {
  workspaces: "workspaces",
  users: "users",
  workspaceacls: "memberships",
  serviceacls: "app permissions",
  workspaceserviceaccesses: "app settings",
  invitations: "invitations",
  apitokens: "API tokens",
  oauthconnections: "connected apps",
  workspaceorgjoinrequests: "join requests",
  files: "file records",
  organizationacls: "organization memberships",
  userdatas: "activity records",
};

const describeCounts = (plans: Plan[]) => {
  const totals: Record<string, number> = {};
  for (const p of plans) {
    for (const [part, n] of Object.entries(p.counts || {})) {
      totals[part] = (totals[part] || 0) + n;
    }
  }
  return Object.entries(totals)
    .map(([part, n]) => `${n} ${PARTS[part] || part}`)
    .join(", ");
};

const describeSkipped = (
  rows: { name: string; skip?: string | null; error?: string }[],
) => rows.map((r) => `• ${r.name} — ${r.skip || r.error}`).join("\n");
const SCAN_HELP = [
  "Lists, without changing anything:",
  "• workspaces with no credits used in 6 months (not an owner's only one, not paid)",
  "• workspaces whose subscription has ended",
  '• test accounts ("test" in the email, QA, fake or throwaway domains) and their workspaces',
].join("\n");

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const EXPORT_PAGE_SIZE = 200; // the backend's largest page

const isoDate = (value?: string | null) =>
  value ? new Date(value).toISOString().slice(0, 10) : "";

/**
 * One CSV cell. Quoted when needed; a leading = + - @ is defused so a name or
 * email a user typed can't run as a formula when the file is opened in Excel.
 */
const csvCell = (value: unknown) => {
  let text = value === undefined || value === null ? "" : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// The same columns as the report script's CSV (scripts/dead-workspaces.mjs in
// cxo-dashboard), plus when it was last used.
const ACTION: Record<string, string> = {
  watching: "DELETE",
  excluded: "KEEP",
  deleted: "DELETED",
  restored: "RESTORED",
};
const CSV_COLUMNS: [string, (c: Candidate) => unknown][] = [
  ["action", (c) => ACTION[c.status || ""] || c.status],
  ["type", (c) => c.type],
  ["id", (c) => c.refId],
  ["name", (c) => c.snapshot?.name || ""],
  [
    "ownerEmail",
    (c) =>
      c.type === "workspace" ? c.snapshot?.ownerEmail : c.snapshot?.email,
  ],
  ["ownerWorkspaces", (c) => c.snapshot?.ownerWorkspaces ?? ""],
  ["createdAt", (c) => isoDate(c.snapshot?.createdAt)],
  ["lastDeductAt", (c) => isoDate(c.snapshot?.lastDeductAt)],
  ["creditsExpireAt", (c) => isoDate(c.snapshot?.creditsExpireAt)],
  ["reason", (c) => c.reason],
  ["lastUsed", (c) => isoDate(lastUsedOf(c))],
];

const toCsv = (rows: Candidate[]) =>
  [
    CSV_COLUMNS.map(([header]) => header),
    ...rows.map((c) => CSV_COLUMNS.map(([, get]) => get(c))),
  ]
    .map((line) => line.map(csvCell).join(","))
    .join("\n");

const downloadFile = (name: string, content: string) => {
  const url = URL.createObjectURL(
    new Blob([content], { type: "text/csv;charset=utf-8" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/** When it was last used — or, once deleted, when someone last came back for it. */
const lastUsedOf = (c: Candidate) =>
  [c.lastUsedAt, c.lastReturnedAt].filter(Boolean).sort().pop();

/** "today, 14:32" / "yesterday, 09:10" / "3 Oct 2026, 14:32". */
const formatWhen = (value?: string | null) => {
  if (!value) return "";
  const at = new Date(value);
  const time = at.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const day = new Date(at).setHours(0, 0, 0, 0);
  const today = new Date().setHours(0, 0, 0, 0);
  if (day === today) return `today, ${time}`;
  if (day === today - 86_400_000) return `yesterday, ${time}`;
  return formatDateTime(value);
};

const PATH_LABEL: Record<string, string> = {
  login: "Logged in",
  signup: "Signed up again",
  "shopify-auto-auth": "Logged in from Shopify",
};

/** One row per person: every app they came from and every place they reached. */
const byPerson = (hits: Hit[]) => {
  const people = new Map<
    string,
    { who: string; from: string[]; where: string[] }
  >();
  for (const h of hits) {
    const who = h.email || h.userId || "-";
    if (!people.has(who)) people.set(who, { who, from: [], where: [] });
    const p = people.get(who)!;
    for (const f of h.sources || []) if (!p.from.includes(f)) p.from.push(f);
    for (const w of h.paths || (h.path ? [h.path] : []))
      if (!p.where.includes(w)) p.where.push(w);
  }
  return [...people.values()];
};

const hasActivity = (c: Candidate) =>
  (c.usedCount || 0) > 0 || (c.returnedCount || 0) > 0;

const isLocalHost = (value?: string | null) =>
  !!value && /localhost|127\.0\.0\.1/.test(value);

/** Which environment the backend is connected to, in plain words. */
/**
 * Which environment the actions change, in plain words. Decided by what the
 * backend is connected to — its database, then its billing — never by where
 * the backend itself runs: a backend on localhost can be pointed at the
 * production database.
 */
const environmentLabel = (env: Environment | null) => {
  if (!env) return null;
  if (isLocalHost(env.database?.host)) {
    return { kind: "local", text: "Local copy" };
  }
  if (/\.test\./.test(env.billing?.url || "") || env.nodeEnv === "test") {
    return { kind: "test", text: "Test" };
  }
  return { kind: "production", text: "Production" };
};

export default function DataCleanup() {
  const [tab, setTab] = useState<Status>("watching");
  const [type, setType] = useState("");
  const [used, setUsed] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [offset, setOffset] = useState(0);

  const [environment, setEnvironment] = useState<Environment | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isActing, setIsActing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [scanError, setScanError] = useState("");

  const [openHits, setOpenHits] = useState<string | null>(null);
  const [hits, setHits] = useState<Hit[]>([]);
  const [hitsLoading, setHitsLoading] = useState(false);

  // Bumped to reload the list after an action.
  const [reloadKey, setReloadKey] = useState(0);

  const fetchSummary = () => {
    getCleanupSummary()
      .then((res) => setSummary(res?.data || null))
      .catch((err) => notify(errorMessage(err), "error"));
  };

  const fetchEnvironment = () => {
    getCleanupEnvironment()
      .then((res) => setEnvironment(res?.data || null))
      .catch((err) => notify(errorMessage(err), "error"));
  };

  useEffect(() => {
    fetchSummary();
    fetchEnvironment();
  }, []);

  // Only the latest request's answer is applied: an older one landing late
  // must not fill the table (or a row's detail) under newer filters.
  const listRequest = useRef(0);
  const hitsRequest = useRef(0);

  useEffect(() => {
    const request = ++listRequest.current;
    setSelected(new Set());
    setOpenHits(null);
    setIsLoading(true);
    getCleanupCandidates({
      status: tab,
      type,
      used,
      search: appliedSearch,
      limit: PAGE_SIZE,
      offset,
    })
      .then((res) => {
        if (request !== listRequest.current) return;
        const rows = res?.data || [];
        const newTotal = res?.total || 0;
        // The last row of the last page went (deleted, kept…): step back to
        // the page that still has rows instead of showing an empty one.
        if (!rows.length && offset > 0 && newTotal > 0) {
          setOffset(Math.floor((newTotal - 1) / PAGE_SIZE) * PAGE_SIZE);
          return;
        }
        setCandidates(rows);
        setTotal(newTotal);
      })
      .catch((err) => {
        if (request !== listRequest.current) return;
        setCandidates([]);
        setTotal(0);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        if (request === listRequest.current) setIsLoading(false);
      });
  }, [tab, type, used, appliedSearch, offset, reloadKey]);

  // A new filter starts from the first page.
  const changeFilter = (apply: () => void) => {
    setOffset(0);
    apply();
  };

  const refresh = () => {
    fetchSummary();
    setReloadKey((k) => k + 1);
  };

  const env = environmentLabel(environment);
  const billingDown = environment?.billing?.reachable === false;

  const handleScan = () => {
    if (
      !window.confirm(
        `Scan ${env?.text || "this environment"}?\n\n${SCAN_HELP}`,
      )
    ) {
      return;
    }
    setIsScanning(true);
    setScanResult(null);
    setScanError("");
    runCleanupScan()
      .then((res) => {
        setScanResult(res?.data || null);
        notify("Scan finished");
        refresh();
      })
      .catch((err) => {
        setScanError(errorMessage(err));
        fetchEnvironment();
      })
      .finally(() => setIsScanning(false));
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected =
    candidates.length > 0 && candidates.every((c) => selected.has(c._id));
  const toggleAll = () => {
    setSelected(
      allSelected ? new Set() : new Set(candidates.map((c) => c._id)),
    );
  };

  const selectedRows = candidates.filter((c) => selected.has(c._id));

  const where = env?.text || "this environment";
  const ids = () => selectedRows.map((c) => c._id);

  const afterAction = (message: string, problems: string) => {
    notify(message);
    if (problems) window.alert(problems);
    setSelected(new Set());
    refresh();
  };

  /** Preview, confirm, then delete. */
  const handleDelete = async () => {
    if (!selectedRows.length) return;
    setIsActing(true);
    try {
      const preview = await deleteCleanupCandidates(ids(), true);
      const plan: Plan[] = preview?.data?.plan || [];
      const go = plan.filter((p) => !p.skip);
      const skip = plan.filter((p) => p.skip);
      const skipText = skip.length
        ? `\n\nWill be skipped (${skip.length}):\n${describeSkipped(skip)}`
        : "";

      if (!go.length) {
        window.alert(`Nothing to delete.${skipText}`);
        return;
      }
      const usedCount = selectedRows.filter(hasActivity).length;
      const warning = usedCount
        ? `\n\n⚠ ${usedCount} of them have been used since they were listed.`
        : "";
      if (
        !window.confirm(
          `Delete ${go.length} item(s) on ${where}?\n\nThis removes: ${describeCounts(go)}.\n\n${DELETE_HELP}${warning}${skipText}`,
        )
      ) {
        return;
      }

      const res = await deleteCleanupCandidates(ids());
      const result: DeleteResult = res?.data || {
        deleted: [],
        skipped: [],
        failed: [],
      };
      // Failed part-way: some of it was archived and removed, so it is in
      // Deleted and Restore puts it back. Otherwise it was left as it was.
      const partly = result.failed.filter((f) => f.partlyDeleted);
      const notDeleted = [
        ...result.skipped,
        ...result.failed.filter((f) => !f.partlyDeleted),
      ];
      const report = [
        notDeleted.length
          ? `Not deleted (still under review):\n${describeSkipped(notDeleted)}`
          : "",
        partly.length
          ? `Partly deleted — moved to Deleted; use Restore to put them back:\n${describeSkipped(partly)}`
          : "",
      ]
        .filter(Boolean)
        .join("\n\n");
      afterAction(
        `Deleted ${result.deleted.length}` +
          (notDeleted.length ? `, ${notDeleted.length} not deleted` : "") +
          (partly.length ? `, ${partly.length} partly deleted` : ""),
        report,
      );
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setIsActing(false);
    }
  };

  const handleRestore = async () => {
    if (
      !selectedRows.length ||
      !window.confirm(
        `Restore ${selectedRows.length} item(s) on ${where}?\n\n${RESTORE_HELP}`,
      )
    ) {
      return;
    }
    setIsActing(true);
    try {
      const res = await restoreCleanupCandidates(ids());
      const result: RestoreResult = res?.data || { restored: [], skipped: [] };
      afterAction(
        `Restored ${result.restored.length}` +
          (result.skipped.length
            ? `, ${result.skipped.length} not restored`
            : ""),
        result.skipped.length
          ? `Not restored:\n${describeSkipped(result.skipped)}`
          : "",
      );
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setIsActing(false);
    }
  };

  const handleKeep = async () => {
    if (
      !selectedRows.length ||
      !window.confirm(`Keep ${selectedRows.length} item(s)?\n\n${KEEP_HELP}`)
    ) {
      return;
    }
    setIsActing(true);
    try {
      const res = await excludeCleanupCandidates(ids());
      const d = res?.data || {};
      afterAction(
        `Kept ${d.excluded ?? 0}` +
          (d.skipped ? ` (${d.skipped} skipped)` : ""),
        "",
      );
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setIsActing(false);
    }
  };

  const handleReview = async () => {
    if (
      !selectedRows.length ||
      !window.confirm(
        `Move ${selectedRows.length} item(s) back to review?\n\n${REVIEW_HELP}`,
      )
    ) {
      return;
    }
    setIsActing(true);
    try {
      const res = await reviewCleanupCandidates(ids());
      const d = res?.data || {};
      afterAction(
        `Moved ${d.reviewing ?? 0} back to review` +
          (d.skipped ? ` (${d.skipped} skipped)` : ""),
        "",
      );
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setIsActing(false);
    }
  };

  /** Every row of this tab with the current filters — all pages, not just this one. */
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const rows: Candidate[] = [];
      for (let from = 0; ; from += EXPORT_PAGE_SIZE) {
        const res = await getCleanupCandidates({
          status: tab,
          type,
          used,
          search: appliedSearch,
          limit: EXPORT_PAGE_SIZE,
          offset: from,
        });
        const page: Candidate[] = res?.data || [];
        rows.push(...page);
        if (page.length < EXPORT_PAGE_SIZE || rows.length >= (res?.total || 0))
          break;
      }
      const label = TABS.find((t) => t.id === tab)!
        .label.toLowerCase()
        .replace(/\s+/g, "-");
      downloadFile(
        `data-cleanup-${label}-${new Date().toISOString().slice(0, 10)}.csv`,
        toCsv(rows),
      );
      notify(`Exported ${rows.length} rows`);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setIsExporting(false);
    }
  };

  const toggleHits = (candidateId: string) => {
    if (openHits === candidateId) {
      setOpenHits(null);
      return;
    }
    setOpenHits(candidateId);
    setHits([]);
    setHitsLoading(true);
    const request = ++hitsRequest.current;
    getCleanupCandidateHits(candidateId)
      .then((res) => {
        if (request === hitsRequest.current) setHits(res?.data || []);
      })
      .catch((err) => notify(errorMessage(err), "error"))
      .finally(() => setHitsLoading(false));
  };

  const count = (status: Status) =>
    summary
      ? (summary[status]?.workspace || 0) + (summary[status]?.user || 0)
      : null;

  const selectable = tab !== "restored";
  // Shown on the action buttons: what they will act on.
  const n = selectedRows.length ? ` (${selectedRows.length})` : "";
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="data-cleanup-page">
      <div className="dashboard-settings-section">
        <div className="section-heading">Data Cleanup</div>

        <div className="settings-container data-cleanup-panel data-cleanup-toolbar">
          <button
            className="version-save-btn"
            onClick={handleScan}
            disabled={isScanning || billingDown}
            title={billingDown ? "Billing is not answering." : SCAN_HELP}
          >
            {isScanning ? "Scanning..." : "Scan for unused"}
          </button>
          {env && (
            <span
              className={`data-cleanup-env data-cleanup-env-${env.kind}`}
              title={`Database: ${environment?.database?.name} on ${environment?.database?.host}\nBilling: ${environment?.billing?.url}\nBackend: ${CENTRAL_DASHBOARD_BACKEND_API}`}
            >
              {env.text}
              {" · "}
              {billingDown ? (
                <span className="data-cleanup-bad">billing down</span>
              ) : (
                <span className="data-cleanup-ok">billing ok</span>
              )}
            </span>
          )}
          {scanError && <span className="data-cleanup-bad">{scanError}</span>}
          {scanResult && (
            <span className="data-cleanup-scan-result">
              Added {scanResult.workspacesListed} workspaces,{" "}
              {scanResult.usersListed} users
              {scanResult.billingFailed > 0 && (
                <span className="data-cleanup-bad">
                  {" "}
                  · {scanResult.billingFailed} skipped (billing didn't answer)
                </span>
              )}
            </span>
          )}
        </div>

        <div className="settings-container data-cleanup-panel">
          <div className="data-cleanup-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={`data-cleanup-tab ${tab === t.id ? "data-cleanup-tab-active" : ""}`}
                onClick={() => changeFilter(() => setTab(t.id))}
                title={t.help}
              >
                {t.label}
                {count(t.id) !== null && (
                  <span className="data-cleanup-tab-count">{count(t.id)}</span>
                )}
              </button>
            ))}
          </div>

          <div className="data-cleanup-toolbar">
            <div className="data-cleanup-filters">
              <select
                className="version-input data-cleanup-select"
                aria-label="Show"
                value={type}
                onChange={(e) => changeFilter(() => setType(e.target.value))}
              >
                <option value="">Workspaces and users</option>
                <option value="workspace">Only workspaces</option>
                <option value="user">Only users</option>
              </select>
              <select
                className="version-input data-cleanup-select"
                aria-label="Used since listed"
                value={used}
                onChange={(e) => changeFilter(() => setUsed(e.target.value))}
              >
                <option value="">Used or not</option>
                <option value="true">Used since listed</option>
                <option value="false">Not used since listed</option>
              </select>
              <input
                className="version-input data-cleanup-search"
                value={search}
                placeholder="Search name or email ↵"
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    changeFilter(() => setAppliedSearch(search.trim()));
                }}
              />
            </div>

            <div className="data-cleanup-actions">
              <button
                className="version-save-btn data-cleanup-secondary-btn"
                onClick={handleExport}
                disabled={isExporting || !total}
                title="Download every row in this tab, with the filters above, as a CSV file."
              >
                {isExporting ? "Exporting..." : "Export CSV"}
              </button>
              {selectable && (
                <>
                  {tab === "watching" && (
                    <>
                      <button
                        className="version-save-btn data-cleanup-danger-btn"
                        onClick={handleDelete}
                        disabled={isActing || !selectedRows.length}
                        title={DELETE_HELP}
                      >
                        Delete{n}
                      </button>
                      <button
                        className="version-save-btn data-cleanup-secondary-btn"
                        onClick={handleKeep}
                        disabled={isActing || !selectedRows.length}
                        title={KEEP_HELP}
                      >
                        Keep{n}
                      </button>
                    </>
                  )}
                  {tab === "excluded" && (
                    <button
                      className="version-save-btn data-cleanup-secondary-btn"
                      onClick={handleReview}
                      disabled={isActing || !selectedRows.length}
                      title={REVIEW_HELP}
                    >
                      Back to review{n}
                    </button>
                  )}
                  {tab === "deleted" && (
                    <button
                      className="version-save-btn"
                      onClick={handleRestore}
                      disabled={isActing || !selectedRows.length}
                      title={RESTORE_HELP}
                    >
                      Restore{n}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="version-subtitle">Loading...</div>
          ) : candidates.length === 0 ? (
            <div className="version-subtitle">Nothing here.</div>
          ) : (
            <div className="data-cleanup-table-scroll">
              <table className="data-cleanup-table">
                <thead>
                  <tr>
                    {selectable && (
                      <th className="data-cleanup-col-check">
                        <input
                          type="checkbox"
                          aria-label="Select all on this page"
                          checked={allSelected}
                          onChange={toggleAll}
                        />
                      </th>
                    )}
                    <th className="data-cleanup-col-kind">Type</th>
                    <th>Name</th>
                    <th>Reason</th>
                    <th
                      className="data-cleanup-col-used"
                      title="When someone last logged in or opened it after it was listed — or came back for it after it was deleted. Click for who, from which app, and where."
                    >
                      Last used
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => {
                    const lastUsed = lastUsedOf(c);
                    const label =
                      c.snapshot?.name || c.snapshot?.email || c.refId;
                    return (
                      <Fragment key={c._id}>
                        <tr
                          className={
                            hasActivity(c) ? "data-cleanup-row-active" : ""
                          }
                        >
                          {selectable && (
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Select ${label}`}
                                checked={selected.has(c._id)}
                                onChange={() => toggle(c._id)}
                              />
                            </td>
                          )}
                          <td>
                            <span
                              className={`data-cleanup-type data-cleanup-type-${c.type}`}
                            >
                              {c.type === "workspace" ? "Workspace" : "User"}
                            </span>
                          </td>
                          <td>
                            <div className="data-cleanup-name">{label}</div>
                            <div className="data-cleanup-sub">
                              {c.type === "workspace"
                                ? c.snapshot?.ownerEmail || "unknown owner"
                                : c.snapshot?.email}
                            </div>
                            <div className="data-cleanup-id">{c.refId}</div>
                          </td>
                          <td className="data-cleanup-reason">{c.reason}</td>
                          <td className="data-cleanup-nowrap">
                            {lastUsed ? (
                              <button
                                className="data-cleanup-link"
                                onClick={() => toggleHits(c._id)}
                                title="Show who, from which app, and where"
                              >
                                {c.lastReturnedAt &&
                                lastUsed === c.lastReturnedAt
                                  ? `Came back ${formatWhen(lastUsed)}`
                                  : formatWhen(lastUsed)}
                                {openHits === c._id ? " ▴" : " ▾"}
                              </button>
                            ) : (
                              <span className="data-cleanup-sub">Not used</span>
                            )}
                          </td>
                        </tr>
                        {openHits === c._id && (
                          <tr className="data-cleanup-hits-row">
                            <td colSpan={selectable ? 5 : 4}>
                              {hitsLoading ? (
                                <div className="version-subtitle">
                                  Loading...
                                </div>
                              ) : hits.length === 0 ? (
                                <div className="version-subtitle">
                                  Nothing recorded.
                                </div>
                              ) : (
                                <table className="data-cleanup-hits">
                                  <thead>
                                    <tr>
                                      <th>Who</th>
                                      <th>From</th>
                                      <th>Where</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {byPerson(hits).map((h) => (
                                      <tr key={h.who}>
                                        <td>{h.who}</td>
                                        <td>{h.from.join(", ") || "-"}</td>
                                        <td className="data-cleanup-id">
                                          {h.where.map((w) => (
                                            <div key={w}>
                                              {PATH_LABEL[w] || w}
                                            </div>
                                          ))}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="data-cleanup-pagination">
              <button
                className="version-save-btn data-cleanup-secondary-btn"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0 || isLoading}
              >
                Previous
              </button>
              <span className="version-subtitle">
                Page {page} of {pages}
              </span>
              <button
                className="version-save-btn data-cleanup-secondary-btn"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={page >= pages || isLoading}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
